import { Button, Checkbox, CheckboxOptionType, Select, Slider, Switch, Tag, Typography, Dropdown, Menu, Tooltip, Input } from "antd"
import './commonComponents.css'
import sortByIcon from '../../assets/icons/sortByIcon.svg'
import React, { useEffect, useMemo, useRef, useState } from "react"
import { ExclamationCircleOutlined, PlusOutlined } from '@ant-design/icons'
import { NavigateFunction, useNavigate } from "react-router-dom"
import { throttle } from "lodash"
import { useAppSelector } from "../../hooks/useTypedSelector"
import lockIcon from "../../assets/icons/lock.svg";
import { useDispatch } from "react-redux";
import { setShowModalRequest } from "../../redux/mobileVerificationSlice";

const { Text } = Typography

const LabelWithTooltip: React.FC<{ label: string; tooltipMessage?: string, allowForDemo?: boolean }> = ({
  label,
  tooltipMessage,
  allowForDemo
}) => {
  const words = (label ?? "").trim().split(/\s+/);
  const lastWord = words.pop() ?? "";
  const rest = words.join(" ");
  const mobileUnverifiedUser = useAppSelector((state: any) => state.mobileVerificationData.mobileUnverifiedUser);
  const isFreeUser = useAppSelector((state: any) => state.userData.freeUser);
  const grey = (mobileUnverifiedUser || isFreeUser) && (allowForDemo !== true)

  return (
    <span className="labelInline">
      <Text className="filterLabel" style={{ color: grey ? "#aeaeae" : "" }}>
        {rest ? rest + " " : ""}
        <span className="lastWordWithTooltip" style={{ color: grey ? "#aeaeae" : "" }}>
          {lastWord}
          {tooltipMessage ? (
            <Tooltip placement="bottom" title={tooltipMessage} arrow overlayClassName="labelTooltip">
              <ExclamationCircleOutlined style={{ cursor: "pointer", marginLeft: 4 }} />
            </Tooltip>
          ) : null}
        </span>
      </Text>
    </span>
  );
};

export const primaryBtn = (
  label: string,
  icon: React.ReactNode = null,
  width: string = '',
  onClick: any = null,
  isAdded: boolean = false
) => {
  return (
    <Button
      type='primary'
      icon={icon}
      className={`addToListBtn ${isAdded ? 'addedBtn' : ''}`}
      style={{ width: width, borderRadius: '15px' }}
      onClick={onClick}
    >
      {label}
    </Button>
  );
};

export const outlinedBtn = (label: string, icon: React.ReactNode = null, onClick: any = null, enableBorderRadius: boolean = false) => {
  return (
    <Button icon={icon} className='outlinedBtn' style={{ borderRadius: enableBorderRadius ? '15px' : '' }}>{label}</Button>
  )
}
export const blackFilledBtn = (label: string, icon: React.ReactNode = null) => {
  return (
    <Button
      icon={
        icon ? (
          <span style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}>
            {icon}
          </span>
        ) : undefined
      }
      className='blackFilledBtn'
    >
      {label}
    </Button>
  );
};


export const iconButton = (icon: React.ReactNode = null) => {
  return (
    <Button icon={icon} className='iconBtn'></Button>
  )
}

interface HoverIconBtnProps {
  label: string;
  iconDefault: string;
  iconHover?: string;
  removeMargin?: boolean
  width?: string
  onClick?: (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
}

export const HoverIconBtn: React.FC<HoverIconBtnProps> = ({ label, iconDefault, iconHover, onClick, removeMargin, width }) => {
  const [hovered, setHovered] = useState(false);

  return (
    <Button
      className={label === "Remove" ? "whiteFilledBtn" : `blackFilledBtn ${label === "Added" && 'Active'}`}
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{ width: width }}
    >
      <img
        src={hovered ? iconHover : iconDefault}
        alt="icon"
        style={{ marginRight: removeMargin ? 0 : 8 }}
      />
      {label}
    </Button>
  );
};

interface Option {
  id: number;
  label: string;
}

interface MultiSelectFilterProps {
  label: string;
  options: Option[];
  allowForDemo?: boolean;
  tooltipMessage?: string;
  value: number[];
  onChange: (value: any[]) => void;
}

export const MultiSelectFilter: React.FC<MultiSelectFilterProps> = ({
  label,
  options,
  allowForDemo,
  value,
  onChange,
  tooltipMessage
}) => {
  const checkboxOptions: CheckboxOptionType[] = options.map(opt => ({
    label: opt.label,
    value: opt.id,
  }));

  const handleClear = React.useCallback(() => {
    if (value.length === 0) return;
    onChange([]);
  }, [value, onChange]);

  const handleCheckboxChange = React.useCallback(
    (optValue: any, checked: boolean) => {
      const addOption = (val: any) => onChange([...value, val]);
      const removeOption = (val: any) => onChange(value.filter((v) => v !== val));

      if (checked) {
        addOption(optValue);
      } else {
        removeOption(optValue);
      }
    },
    [value, onChange]
  );

  const mobileUnverifiedUser = useAppSelector((state: any) => state.mobileVerificationData.mobileUnverifiedUser);
  const isFreeUser = useAppSelector((state: any) => state.userData.freeUser);
  const grey = (mobileUnverifiedUser || isFreeUser) && allowForDemo !== true;

  return (
    <div className="filterGroup" style={{ color: grey ? "#aeaeae" : "" }}>
      <div className="filterHeader">
        <div className="labelTootlipConatiner">
          <LabelWithTooltip label={label} tooltipMessage={tooltipMessage} allowForDemo={allowForDemo} />
        </div>
        {grey ? <img src={lockIcon} alt="lock" style={{ width: "12px", height: "12px" }} /> : <Button type="text" style={{ color: '#0056D2' }} onClick={handleClear}>Clear</Button>}
      </div>
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 8,
          maxHeight: 120,
          overflowY: 'auto',
        }}
      >
        {checkboxOptions.map(opt => (
          <label key={opt.value} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <Checkbox
              checked={value.includes(opt.value)}
              onChange={e => handleCheckboxChange(opt.value, e.target.checked)}
              value={opt.value}
            />
            <span>{opt.label}</span>
          </label>
        ))}
      </div>
    </div>
  );
};

interface SwitchFilterProps {
  label: string;
  tooltipMessage?: string;
  allowForDemo?: boolean;
  defaultForDemo?: boolean;
  value: boolean;
  onChange: (value: boolean) => void;
}

export const SwitchFilter: React.FC<SwitchFilterProps> = ({
  label, tooltipMessage, allowForDemo, defaultForDemo, value, onChange
}) => {
  const mobileUnverifiedUser = useAppSelector(
    (state: any) => state.mobileVerificationData.mobileUnverifiedUser
  );
  const isFreeUser = useAppSelector((state: any) => state.userData.freeUser);

  const isForcedOn = mobileUnverifiedUser && defaultForDemo;
  const grey = (mobileUnverifiedUser || isFreeUser) && allowForDemo !== true;
  const switchChecked = isForcedOn ? true : value;
  const switchDisabled = isForcedOn;

  const handleSwitchChange = React.useCallback((val: boolean) => {
    if (isForcedOn) {
      onChange(true);
      return;
    }
    onChange(val);
  }, [isForcedOn, onChange]);


  return (
    <div className="filterGroup">
      <div className="filterHeader">
        <LabelWithTooltip
          label={label}
          tooltipMessage={tooltipMessage}
          allowForDemo={allowForDemo}
        />

        {grey && (
          <img src={lockIcon} alt="lock" style={{ width: 12, height: 12 }} />
        )}

        <Switch
          checked={switchChecked}
          onChange={handleSwitchChange}
          disabled={switchDisabled}
          style={{
            marginTop: 4,
            opacity: isForcedOn ? 0.7 : 1,
            cursor: isForcedOn ? "not-allowed" : "pointer"
          }}
        />
      </div>
    </div>
  );
};

interface RangeSliderFilterProps {
  label: string;
  tooltipMessage?: string;
  allowForDemo?: boolean;
  value: { min: number | null; max: number | null };
  min: number;
  max: number;
  step: number;
  prefix: string;
  suffix: string;
  isClearAll?: boolean;
  applyClick: (value: { min: number | null; max: number | null }) => void;
}

const RangeSliderFilterComponent: React.FC<RangeSliderFilterProps> = ({
  label,
  tooltipMessage,
  allowForDemo,
  value,
  step,
  min,
  max,
  prefix,
  suffix,
  applyClick,
  isClearAll,
}) => {
  const [localValue, setLocalValue] = useState<{ min: number | null; max: number | null }>(value);
  const dispatch = useDispatch();
  const mobileUnverifiedUser = useAppSelector((state: any) => state.mobileVerificationData.mobileUnverifiedUser);
  const isFreeUser = useAppSelector((state: any) => state.userData.freeUser);
  const grey = (mobileUnverifiedUser || isFreeUser) && allowForDemo !== true;

  useEffect(() => {
    setLocalValue(value);
  }, [value, isClearAll, allowForDemo]);

  const handleClear = () => {
    const cleared = { min: null, max: null };
    if (localValue.min === null && localValue.max === null) {
      return;
    }
    setLocalValue(cleared);
    applyClick(cleared);
  };

  const navigate = useNavigate();

  const handleApply = () => {
    if (grey) {
      if (isFreeUser) {
        navigate("/upgrade");
        return;
      }
      sessionStorage.setItem("mobile_verify_redirect", window.location.pathname);
      dispatch(setShowModalRequest(true));
      return;
    }
    const prev = value || { min: null, max: null };
    const hasChanged = (localValue?.min !== prev?.min) || (localValue?.max !== prev?.max);
    if (!hasChanged) return;
    applyClick(localValue);
  };

  const throttledChange = useRef<(val: number[]) => void>(
    throttle((val: number[]) => {
      const [minVal, maxVal] = val;
      setLocalValue({ min: minVal, max: maxVal });
    }, 100)
  ).current;

  const marks = useMemo(() => ({
    [min]: `${prefix}${min}${suffix}`,
    [max]: {
      style: {
        marginLeft: "-10px",
        whiteSpace: "nowrap",
        fontSize: "12px",
      },
      label: (
        <span style={{ whiteSpace: "nowrap" }}>
          {`${prefix}${max}\u00A0${suffix}`}
        </span>
      ),
    },
  }), [min, max, prefix, suffix]);

  return (
    <div className="filterGroup">
      <div className="filterHeader">
        <div className="labelTootlipConatiner">
          <LabelWithTooltip label={label} tooltipMessage={tooltipMessage} allowForDemo={allowForDemo} />
        </div>
        {grey ? <img src={lockIcon} alt="lock" style={{ width: "12px", height: "12px" }} /> : <Button type="text" onClick={handleClear} style={{ color: "#0056D2" }}>
          Clear
        </Button>}
      </div>

      <div className="sliderWrapper" style={{ color: grey ? "#aeaeae" : "" }}>
        <Slider
          range
          min={min}
          max={max}
          step={step}
          marks={marks}
          value={[localValue.min ?? min, localValue.max ?? max]}
          onChange={throttledChange}
          className="sliderFilter"
        />
      </div>

      <div className="filterApplyBtn" style={{ color: grey ? "#aeaeae" : "" }}>
        <Button
          type="primary"
          className="addToListBtn"
          style={{ borderRadius: "15px", backgroundColor: grey ? "#cccddd" : "" }}
          onClick={handleApply}
        >
          Apply
        </Button>
      </div>
    </div>
  );
};

export const RangeSliderFilter = React.memo(RangeSliderFilterComponent);

interface MultiSliderFilterProps {
  label: string;
  tooltipMessage?: string;
  allowForDemo?: boolean;
  value: {
    id: number;
    value: {
      min: number;
      max: number;

    };
  }[];
  options: any[]
  applyClick: (value: {
    id: number;
    value: {
      min: number;
      max: number;
    };
  }[]) => void;
  isClearAll?: boolean
}

export const MultiSliderFilter: React.FC<MultiSliderFilterProps> = ({ label, value, tooltipMessage, allowForDemo, options, applyClick, isClearAll }) => {
  const [localValue, setLocalValue] = useState(() => value?.length ? value.map(v => ({ id: v.id, value: { min: v.value.min, max: v.value.max } })) : []
  );

  useEffect(() => {
    setLocalValue(value);
  }, [value, isClearAll]);

  const handleSliderChange = React.useCallback((id: number, range: [number, number]) => {
    setLocalValue(prev => {
      const updated = [...prev];
      const index = updated.findIndex(item => item.id === id);
      if (index !== -1) {
        updated[index] = { id, value: { min: range[0], max: range[1] } };
      } else {
        updated.push({ id, value: { min: range[0], max: range[1] } });
      }
      return updated;
    });
  }, []);

  const dispatch = useDispatch();
  const mobileUnverifiedUser = useAppSelector((state: any) => state.mobileVerificationData.mobileUnverifiedUser);
  const isFreeUser = useAppSelector((state: any) => state.userData.freeUser);
  const grey = (mobileUnverifiedUser || isFreeUser) && allowForDemo !== true;


  const getSliderValue = (id: number): [number, number] => {
    const slider = localValue.find(item => item.id === id);
    const option = options.find(opt => opt.id === id);
    return slider ? [slider.value.min, slider.value.max] : [option?.min || 0, option?.max || 100];
  };

  const handleClear = React.useCallback(() => {
    if (localValue.length === 0) return;
    setLocalValue([]);
    applyClick([]);
  }, [localValue, applyClick]);
  // compare arrays of {id, value:{min,max}}
  const arraysEqual = (a: any[] = [], b: any[] = []) => {
    if (a.length !== b.length) return false;
    for (const item of a) {
      const match = b.find(x => x.id === item.id);
      if (!match) return false;
      if ((match.value?.min ?? null) !== (item.value?.min ?? null)) return false;
      if ((match.value?.max ?? null) !== (item.value?.max ?? null)) return false;
    }
    return true;
  };

  const navigate = useNavigate();

  const handleApply = () => {
    if (grey) {
      if (isFreeUser) {
        navigate("/upgrade");
        return;
      }
      sessionStorage.setItem("mobile_verify_redirect", window.location.pathname);
      dispatch(setShowModalRequest(true));
      return;
    }
    const prev = value || [];
    if (arraysEqual(prev, localValue || [])) return;
    applyClick(localValue || []);
  };


  return (
    <div className="filterGroup">
      <div className="filterHeader">
        <div className="labelTootlipConatiner">
          <LabelWithTooltip label={label} tooltipMessage={tooltipMessage} allowForDemo={allowForDemo} />
        </div>
        {grey ? <img src={lockIcon} alt="lock" style={{ width: "12px", height: "12px" }} /> : <Button type="text" style={{ color: "#0056D2" }} onClick={handleClear}>Clear</Button>}
      </div>
      {options.map((item) => (
        <div key={item.id}>
          <Text>{item.label}</Text>
          <Slider
            range
            min={item.min}
            max={item.max}
            step={item.step}
            marks={{
              [item.min]: {
                label: `${item.prefix || ""}${item.min} ${item.suffix || ""}`,
                style: { marginLeft: "10px" }
              },
              [item.max]: {
                label: `${item.prefix || ""}${item.max} ${item.suffix || ""}`,
                style: { marginLeft: "-12px" }
              }
            }}
            value={getSliderValue(item.id)}
            onChange={(val) => handleSliderChange(item.id, val as [number, number])}
            className="sliderFilter"
          />
        </div>
      ))}

      <div className="filterApplyBtn">
        <Button
          type="primary"
          className="addToListBtn"
          style={{ borderRadius: "15px", backgroundColor: grey ? "#cccddd" : "" }}
          onClick={handleApply}
        >
          Apply
        </Button>
      </div>
    </div>
  );
};

interface SliderWithDropdownProps {
  label: string;
  tooltipMessage?: string;
  allowForDemo?: boolean;
  value: {
    id: number;
    value: {
      min: number;
      max: number;
    };
  }[];
  options: any[];
  applyClick: (value: {
    id: number;
    value: {
      min: number;
      max: number;
    };
  }[]) => void;
  prefix?: string
  suffix?: string
  isClearAll?: boolean
}

interface BlockType {
  dropdown: number | null;
  value: {
    min: number | null;
    max: number | null;
  };
}

export const SliderWithDropdown: React.FC<SliderWithDropdownProps> = ({
  label,
  tooltipMessage,
  options,
  allowForDemo,
  value,
  applyClick,
  prefix,
  suffix,
  isClearAll
}) => {
  const [blocks, setBlocks] = useState<BlockType[]>(() => {
    return value?.length ? value.map(v => ({
      dropdown: v.id,
      value: {
        min: v.value.min,
        max: v.value.max
      }
    }))
      : [{ dropdown: null, value: { min: null, max: null } }];
  });
  useEffect(() => {
    if (isClearAll) {
      setBlocks([{ dropdown: null, value: { min: null, max: null } }]);
    }
  }, [isClearAll]);

  useEffect(() => {
    if (value?.length) {
      setBlocks(
        value.map(v => ({
          dropdown: v.id,
          value: { min: v.value.min, max: v.value.max }
        }))
      );
    } else {
      setBlocks([{ dropdown: null, value: { min: null, max: null } }]);
    }
  }, [value]);

  const dispatch = useDispatch();
  const mobileUnverifiedUser = useAppSelector((state: any) => state.mobileVerificationData.mobileUnverifiedUser);
  const isFreeUser = useAppSelector((state: any) => state.userData.freeUser);
  const grey = (mobileUnverifiedUser || isFreeUser) && allowForDemo !== true;

  const handleDropdownChange = (index: number, val: number) => {
    const option = options.find(opt => opt.id === val);
    const updated = [...blocks];
    updated[index] = {
      dropdown: val,
      value: {
        min: option?.min ?? null,
        max: option?.max ?? null
      }
    };
    setBlocks(updated);
  };

  const handleSliderChange = (index: number, val: [number, number]) => {
    const updated = [...blocks];
    updated[index].value = {
      min: val[0],
      max: val[1]
    };
    setBlocks(updated);
  };

  const handleAddBlock = () => {
    setBlocks([...blocks, { dropdown: null, value: { min: null, max: null } }]);
  };

  const navigate = useNavigate();

  const handleApply = () => {
    if (grey) {
      if (isFreeUser) {
        navigate("/upgrade");
        return;
      }
      sessionStorage.setItem("mobile_verify_redirect", window.location.pathname);
      dispatch(setShowModalRequest(true));
      return;
    }
    const formatted = blocks
      .filter(b => b.dropdown !== null)
      .map(b => {
        const selectedOption = options.find(opt => opt.id === b.dropdown)!;

        const min = b.value.min ?? selectedOption.min;
        const max = b.value.max ?? selectedOption.max;

        return {
          id: b.dropdown!,
          value: {
            min,
            max
          }
        };
      });

    const arraysEqual = (a: any[] = [], b: any[] = []) => {
      if (a.length !== b.length) return false;
      for (const item of a) {
        const match = b.find(x => x.id === item.id);
        if (!match) return false;
        if ((match.value?.min ?? null) !== (item.value?.min ?? null)) return false;
        if ((match.value?.max ?? null) !== (item.value?.max ?? null)) return false;
      }
      return true;
    };

    if (!arraysEqual(formatted, value || [])) {
      applyClick(formatted);
    }
  };

  const handleClear = () => {
    const isAlreadyCleared = blocks.length === 1 &&
      blocks[0].dropdown === null &&
      blocks[0].value.min === null &&
      blocks[0].value.max === null;

    if (isAlreadyCleared) {
      return;
    }
    setBlocks([{ dropdown: null, value: { min: null, max: null } }]);
    applyClick([]);
  };



  return (
    <div className="filterGroup">
      <div className="filterHeader">
        <div className="labelTootlipConatiner">
          <LabelWithTooltip label={label} tooltipMessage={tooltipMessage} allowForDemo={allowForDemo} />
        </div>
        {grey ? <img src={lockIcon} alt="lock" style={{ width: "12px", height: "12px" }} /> : <Button type="text" onClick={handleClear} style={{ color: "#0056D2" }}>
          Clear
        </Button>}
      </div>
      {blocks.map((block, index) => {
        const selectedOption = options.find(opt => opt.id === block.dropdown);
        const isLast = index === blocks.length - 1;
        return (
          <div key={`${block.dropdown}-${index}`} style={{ marginBottom: 20 }}>
            <div className="dropdownSelectContainer">
              <Select
                allowClear
                placeholder="Select"
                value={block.dropdown}
                onChange={val => handleDropdownChange(index, val)}
                className="filterDropdown"
              >
                {options
                  .filter(
                    opt =>
                      !blocks.some(
                        (b, i) => b.dropdown === opt.id && i !== index
                      )
                  )
                  .map(opt => (
                    <Select.Option key={opt.id} value={opt.id}>
                      {opt.label}
                    </Select.Option>
                  ))}
              </Select>
              {selectedOption && isLast && options.some(opt => !blocks.some(b => b.dropdown === opt.id)) && (
                <Button icon={<PlusOutlined />} onClick={handleAddBlock} />
              )}

            </div>
            {selectedOption && (
              <Slider
                range
                min={selectedOption.min}
                max={selectedOption.max}
                step={selectedOption.step}
                marks={{
                  [selectedOption.min]: `${prefix || ""}${selectedOption.min}${suffix || ""}`,
                  [selectedOption.max]: `${prefix || ""}${selectedOption.max}${suffix || ""}`
                }}
                value={[
                  block.value.min ?? selectedOption.min,
                  block.value.max ?? selectedOption.max
                ]}
                onChange={val =>
                  handleSliderChange(index, val as [number, number])
                }
                className="sliderFilter"
              />
            )}
          </div>
        );
      })}

      <div className="filterApplyBtn">
        <Button
          type="primary"
          className="addToListBtn"
          style={{ borderRadius: "15px", backgroundColor: grey ? "#cccddd" : "" }}
          onClick={handleApply}
        >
          Apply
        </Button>
      </div>
    </div>
  );
};

interface SliderMinMaxProps {
  label: string;
  tooltipMessage?: string;
  allowForDemo?: boolean;
  value: {
    min: number | null;
    max: number | null;
    dropdownMin: number | null;
    dropdownMax: number | null;
  };

  min: number;
  max: number;
  step: number;
  prefix: string;
  suffix: string;
  minOptions: { id: number; label: string }[];
  maxOptions: { id: number; label: string }[];
  applyClick: (value: {
    min: number | null;
    max: number | null;
    dropdownMin: number | null;
    dropdownMax: number | null;
  }) => void;

  isClearAll?: boolean
}

export const SliderMinMax: React.FC<SliderMinMaxProps> = ({
  label,
  tooltipMessage,
  allowForDemo,
  value,
  min,
  max,
  step,
  prefix,
  suffix,
  minOptions,
  maxOptions,
  isClearAll,
  applyClick
}) => {
  const [localValue, setLocalValue] = useState(value);

  const formatNumber = (num: number | string) => {
    const n = Number(num);
    if (isNaN(n)) return num; // if it's something weird, leave it

    if (n >= 1_000_000_000) return (n / 1_000_000_000).toFixed(1).replace(/\.0$/, "") + "B";
    if (n >= 1_000_000) return (n / 1_000_000).toFixed(1).replace(/\.0$/, "") + "M";
    if (n >= 1_000) return (n / 1_000).toFixed(1).replace(/\.0$/, "") + "k";

    return n.toString();
  };

  useEffect(() => {
    setLocalValue(value);
  }, [value, isClearAll]);

  const handleSliderChange = (val: number[]) => {
    setLocalValue(prev => ({
      ...prev,
      min: val[0],
      max: val[1],
      dropdownMin: val[0] !== prev?.dropdownMin ? null : prev?.dropdownMin,
      dropdownMax: val[1] !== prev?.dropdownMax ? null : prev?.dropdownMax,
    }));
  };

  const handleDropdownChange = (
    key: "dropdownMin" | "dropdownMax",
    val: number
  ) => {
    const selectedLabel = key === "dropdownMin"
      ? minOptions.find(opt => opt.id === val)?.label
      : maxOptions.find(opt => opt.id === val)?.label;

    const parseFormatted = (value: number | string | null | undefined) => {
      if (value == null) return NaN;

      const str = String(value);
      if (str.endsWith("k")) return Number(str.replace("k", "")) * 1000;
      if (str.endsWith("M")) return Number(str.replace("M", "")) * 1_000_000;
      if (str.endsWith("B")) return Number(str.replace("B", "")) * 1_000_000_000;

      return Number(str);
    };


    const parsed = parseFormatted(selectedLabel || "");
    setLocalValue(prev => {
      const updated = {
        ...prev,
        [key]: val
      };

      if (!isNaN(parsed)) {
        if (key === "dropdownMin") updated.min = parsed;
        else updated.max = parsed;
      }

      return updated;
    });
  };

  const dispatch = useDispatch()
  const mobileUnverifiedUser = useAppSelector((state: any) => state.mobileVerificationData.mobileUnverifiedUser);
  const isFreeUser = useAppSelector((state: any) => state.userData.freeUser);
  const grey = (mobileUnverifiedUser || isFreeUser) && allowForDemo !== true;


  const navigate = useNavigate();

  const handleApply = () => {
    if (grey) {
      if (isFreeUser) {
        navigate("/upgrade");
        return;
      }
      sessionStorage.setItem("mobile_verify_redirect", window.location.pathname);
      dispatch(setShowModalRequest(true));
      return;
    }
    const hasChanged =
      localValue?.min !== value?.min ||
      localValue?.max !== value?.max ||
      localValue?.dropdownMin !== value?.dropdownMin ||
      localValue?.dropdownMax !== value?.dropdownMax;

    if (!hasChanged) return;

    const fixed = {
      min: localValue?.min ?? min,
      max: localValue?.max ?? max,
      dropdownMin: localValue?.dropdownMin ?? null,
      dropdownMax: localValue?.dropdownMax ?? null,
    };

    applyClick(fixed);
  };



  const handleClear = () => {
    const isAlreadyCleared = !localValue?.min && !localValue?.max && !localValue?.dropdownMin && !localValue?.dropdownMax;

    if (isAlreadyCleared) {
      return;
    }

    const cleared = {
      min: null,
      max: null,
      dropdownMin: null,
      dropdownMax: null,
    };

    setLocalValue(cleared);
    applyClick(cleared);
  };

  return (
    <div className="filterGroup">
      <div className="filterHeader">
        <div className="labelTootlipConatiner">
          <LabelWithTooltip label={label} tooltipMessage={tooltipMessage} allowForDemo={allowForDemo} />
        </div>
        {grey ? <img src={lockIcon} alt="lock" style={{ width: "12px", height: "12px" }} /> : <Button type="text" onClick={handleClear} style={{ color: "#0056D2" }}>
          Clear
        </Button>}
      </div>
      <div className="sliderWrapper">
        <Slider
          className="sliderFilter"
          range
          min={min}
          max={max}
          step={step}
          value={[
            localValue?.min ?? min,
            localValue?.max ?? max,
          ]}
          marks={{
            [min]: `${prefix}${min}${suffix}`,
            [max]: {
              style: {
                marginLeft: "-10px",
                whiteSpace: "nowrap",
                fontSize: "12px",
              },
              label: (
                <span style={{ whiteSpace: "nowrap", marginLeft: "20px" }}>
                  {`${prefix}${max}\u00A0${suffix}`}
                </span>
              ),
            },
          }}
          onChange={handleSliderChange}
        />

      </div>

      <div className="filterApplyBtn" style={{ justifyContent: "space-between" }}>
        <div className="filterMinMax" style={{ display: "flex", gap: "0.5vw" }}>
          <Select
            placeholder="Min"
            className="minFilter"
            value={localValue?.dropdownMin ?? undefined}
            onChange={(val) => handleDropdownChange("dropdownMin", val)}
          >
            {minOptions.map((opt) => (
              <Select.Option key={opt.id} value={opt.id} className="dropdownOption">
                <div className="dropdownOptionLabel">{formatNumber(opt.label)}</div>
              </Select.Option>

            ))}
          </Select>

          <Select
            placeholder="Max"
            className="maxFilter"
            value={localValue?.dropdownMax ?? undefined}
            onChange={(val) => handleDropdownChange("dropdownMax", val)}
          >
            {maxOptions.map((opt) => (
              <Select.Option key={opt.id} value={opt.id} className="dropdownOption">
                <div className="dropdownOptionLabel">{formatNumber(opt.label)}</div>
              </Select.Option>

            ))}
          </Select>
        </div>
        <Button
          type="primary"
          className="pillApplyBtn"
          onClick={handleApply}
          style={{ backgroundColor: grey ? "#cccddd" : "" }}
        >
          Apply
        </Button>

      </div>
    </div>
  );
};

interface ChipSelectProps {
  label: string;
  tooltipMessage?: string;
  allowForDemo?: boolean;
  value: string[];
  options: string[];
  onChange: (value: number | null) => void;
}

export const ChipSelect: React.FC<ChipSelectProps> = ({
  label,
  value,
  allowForDemo,
  options,
  onChange,
  tooltipMessage
}) => {
  const checkboxOptions = options.map((opt: any) => ({
    label: opt.label,
    value: opt.id,
  }));

  const isValueApplied = value !== null && value !== undefined && (!Array.isArray(value) || value.length > 0);

  const handleClear = React.useCallback(() => {
    if (!isValueApplied) return;
    onChange(null);
  }, [isValueApplied, onChange]);

  const handleTagClick = React.useCallback((optValue: any) => {
    onChange(value === optValue ? null : optValue);
  }, [value, onChange]);

  const mobileUnverifiedUser = useAppSelector((state: any) => state.mobileVerificationData.mobileUnverifiedUser);
  const isFreeUser = useAppSelector((state: any) => state.userData.freeUser);
  const grey = (mobileUnverifiedUser || isFreeUser) && allowForDemo !== true;

  return (
    <div className="filterGroup">
      <div className="filterHeader">
        <div className="labelTootlipConatiner">
          <LabelWithTooltip label={label} tooltipMessage={tooltipMessage} allowForDemo={allowForDemo} />
        </div>
        {grey ? <img src={lockIcon} alt="lock" style={{ width: "12px", height: "12px" }} /> : <Button type="text" onClick={handleClear} style={{ color: '#0056D2' }}>
          Clear
        </Button>}
      </div>

      <div className="chipWrapper">
        {checkboxOptions.map((opt) => {
          const isLong = opt.label.length > 20;
          const displayLabel = isLong ? `${opt.label.substring(0, 20)}...` : opt.label;

          return (
            <Tooltip title={isLong ? opt.label : ''} key={opt.value}>
              <Tag
                className={value === opt.value ? 'chip active' : 'chip'}
                onClick={handleTagClick.bind(null, opt.value)}
              >
                {displayLabel}
              </Tag>
            </Tooltip>
          );
        })}
      </div>
    </div>
  );
};

interface MultiSelectDropdownProps {
  label: string;
  tooltipMessage?: string;
  allowForDemo?: boolean;
  value: { dropdown: number; multiSelect: number[] }[];
  options: {
    id: number;
    label: string;
    value: { id: number; label: string }[];
  }[];
  onChange: (value: { dropdown: number | null; multiSelect: number[] }[]) => void;
}
interface DropdownCheckbox {
  dropdown: number | null;
  multiSelect: number[];
}

export const MultiSelectDropdown: React.FC<MultiSelectDropdownProps> = ({ label, value, allowForDemo, tooltipMessage, options, onChange }) => {
  const [blocks, setBlocks] = useState<DropdownCheckbox[]>(() => {
    return value?.length
      ? value.map((v) => ({
        dropdown: v.dropdown ?? null,
        multiSelect: v.multiSelect ?? []
      }))
      : [{ dropdown: null, multiSelect: [] }];
  });

  useEffect(() => {
    if (!value || value.length === 0) {
      setBlocks([{ dropdown: null, multiSelect: [] }]);
    } else {
      setBlocks(
        value.map((v) => ({
          dropdown: v.dropdown ?? null,
          multiSelect: v.multiSelect ?? []
        }))
      );
    }
  }, [value]);

  const handleDropdownChange = React.useCallback((index: number, val: number) => {
    setBlocks(prev => prev.map((block, i) =>
      i === index
        ? { ...block, dropdown: val, multiSelect: [] }
        : { ...block }
    ));
  }, []);

  const handleCheckboxChange = React.useCallback((index: number, checkedValues: number[]) => {
    setBlocks(prev => {
      const updated = prev.map((block, i) =>
        i === index
          ? { ...block, multiSelect: [...checkedValues] }
          : block
      );
      const filtered = updated.filter(
        (block) => block.multiSelect.length > 0
      );
      onChange(filtered);
      return updated;
    });
  }, [onChange]);

  const handleAddBlock = React.useCallback(() => {
    setBlocks(prev => [...prev, { dropdown: null, multiSelect: [] }]);
  }, []);

  const handleClear = React.useCallback(() => {
    const isAnythingSelected = blocks.some(
      (b) => b.dropdown !== null || b.multiSelect.length > 0
    );
    if (!isAnythingSelected) return;
    onChange([]);
    setBlocks([{ dropdown: null, multiSelect: [] }]);
  }, [blocks, onChange]);

  const mobileUnverifiedUser = useAppSelector((state: any) => state.mobileVerificationData.mobileUnverifiedUser);
  const isFreeUser = useAppSelector((state: any) => state.userData.freeUser);
  const grey = (mobileUnverifiedUser || isFreeUser) && allowForDemo !== true;

  return (
    <div className="filterGroup">
      <div className="filterHeader">
        <div className="labelTootlipConatiner">
          <LabelWithTooltip label={label} tooltipMessage={tooltipMessage} allowForDemo={allowForDemo} />
        </div>
        {grey ? <img src={lockIcon} alt="lock" style={{ width: "12px", height: "12px" }} /> : <Button
          type="text"
          style={{ color: "#0056D2" }}
          onClick={handleClear}
        >
          Clear
        </Button>}
      </div>
      {blocks.map((block, index) => {
        const selectedOption = options.find(opt => opt.id === block.dropdown);
        return (
          <div key={`${block.dropdown}-${index}`} style={{ marginBottom: '20px' }}>
            <div style={{ display: 'flex', gap: '1vw' }}>
              <Select
                allowClear
                placeholder="Select"
                value={block.dropdown}
                onChange={handleDropdownChange.bind(null, index)}
                className="filterDropdown"
              >
                {options
                  .filter(opt => !blocks.some((b, i) => b.dropdown === opt.id && i !== index))
                  .map(opt => (
                    <Select.Option key={opt.id} value={opt.id}>
                      {opt.label}
                    </Select.Option>
                  ))}
              </Select>

              {index + 1 !== options.length && block.multiSelect.length > 0 && index === blocks.length - 1 && (
                <Button onClick={handleAddBlock} icon={<PlusOutlined />} />
              )}
            </div>

            {block.dropdown !== null && selectedOption?.value && (
              <Checkbox.Group
                options={selectedOption.value.map((opt: any) => ({ label: opt.label, value: opt.id }))}
                value={block.multiSelect}
                onChange={handleCheckboxChange.bind(null, index)}
                style={{ display: 'block', margin: '10px 0' }}
              />
            )}
          </div>
        );
      })}
    </div>
  );
};

interface OptionType {
  id: number;
  label: string;
}

interface SearchableMultiSelectProps {
  label: string;
  tooltipMessage?: string;
  allowForDemo?: boolean;
  options: OptionType[];
  value: number[];
  onChange: (value: number[]) => void;
  isClearAll?: boolean
}

const SearchableMultiSelectComponent: React.FC<SearchableMultiSelectProps> = ({
  label,
  options,
  allowForDemo,
  value,
  onChange,
  tooltipMessage,
  isClearAll
}) => {
  const [search, setSearch] = useState("");

  useEffect(() => {
    if (isClearAll) {
      setSearch("")
    }
  }, [isClearAll]);

  const filteredOptions = options
    ?.filter((opt) => {
      if (!search || search.trim() === "") return true;
      return opt.label?.toLowerCase()?.includes(search?.toLowerCase());
    })

  const handleCheckboxChange = (checkedValues: any) => {
    onChange(checkedValues as number[]);
  };

  const handleClear = () => {
    if (!value || value.length === 0) return;
    onChange([]);
    setSearch("");
  };

  const mobileUnverifiedUser = useAppSelector((state: any) => state.mobileVerificationData.mobileUnverifiedUser);
  const isFreeUser = useAppSelector((state: any) => state.userData.freeUser);
  const grey = (mobileUnverifiedUser || isFreeUser) && allowForDemo !== true;

  return (
    <div className="filterGroup">
      <div className="filterHeader">
        <div className="labelTootlipConatiner">
          <LabelWithTooltip label={label} tooltipMessage={tooltipMessage} allowForDemo={allowForDemo} />
        </div>
        {grey ? <img src={lockIcon} alt="lock" style={{ width: "12px", height: "12px" }} /> : <Button type="text" style={{ color: '#0056D2' }} onClick={handleClear}>Clear</Button>}
      </div>
      <Input
        placeholder="Search"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        style={{ marginBottom: 8 }}
        allowClear
      />
      <Checkbox.Group
        style={{ display: 'flex', flexDirection: 'column', flexWrap: 'nowrap', gap: 6, maxHeight: 120, overflowY: 'auto' }}
        value={value}
        onChange={handleCheckboxChange}
      >
        {filteredOptions?.map((opt) => (
          <Checkbox key={opt.id} value={opt.id}>
            {opt.label}
          </Checkbox>
        ))}
      </Checkbox.Group>
    </div>
  );
};

const SearchableMultiSelect = React.memo(SearchableMultiSelectComponent);

export default SearchableMultiSelect;



interface SortOption {
  label: string;
  value: string;
}

const sortOptions: SortOption[] = [
  { label: 'Popularity', value: 'popularity' },
  { label: 'College Name (A to Z)', value: 'collegeName' },
  { label: 'Tuition Fee (Low to High)', value: 'tuitionFee' },
  { label: 'Test Score by SAT (Low to High)', value: 'testScore' },
  { label: 'Ranking', value: 'ranking' },
];

interface SortDropdownProps {
  onSelect: (value: SortOption) => void;
  value: string;
}

export const SortDropdown: React.FC<SortDropdownProps> = ({ onSelect, value }) => {
  const [isHovered, setIsHovered] = useState(false);

  const handleMouseEnter = React.useCallback(() => setIsHovered(true), []);
  const handleMouseLeave = React.useCallback(() => setIsHovered(false), []);
  const handleSelect = React.useCallback((option: SortOption) => onSelect(option), [onSelect]);

  const menu = (
    <Menu
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {sortOptions.map(option => (
        <Menu.Item
          key={option.value}
          onClick={handleSelect.bind(null, option)}
          className={`customMenuItem ${!isHovered && value === option.value ? 'customMenu-option-selected' : ''}`}
        >
          {option.label}
        </Menu.Item>
      ))}
    </Menu>
  );

  return (
    <Dropdown
      overlay={menu}
      placement="bottomRight"
      trigger={['click']}
      overlayStyle={{ zIndex: 'auto' }}
    >
      <Button
        shape="circle"
        icon={
          <img
            src={sortByIcon}
            alt="sort"
            style={{ height: 25, width: 23, marginTop: 6, marginRight: 1 }}
          />
        }
      />
    </Dropdown>
  );
};


export const scrollToCollegeDetail = (navigate: NavigateFunction, slug: string) => {
  window.scrollTo({ top: 0, behavior: 'smooth' });
  navigate(`/college/${slug}`);
};
